import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RefreshProvider } from './contexts/RefreshContext';
import Layout from './components/layout/Layout';
import ExtensionErrorGuard from './components/ExtensionErrorGuard';
import Login from './components/auth/Login';
import AccessDenied from './pages/AccessDenied';
import Dashboard from './pages/dashboard/Dashboard';
import Users from './pages/users/Users';
import Vendors from './pages/vendors/Vendors';
import VendorEdit from './pages/vendors/VendorEdit';
import VendorActivity from './pages/vendors/VendorActivity/VendorActivity';
import CreateTransaction from './pages/vendors/CreateTransaction';
import TestingAccount from './pages/testing-account/TestingAccount';
import BankDeposits from './pages/bank-deposits/BankDeposits';
import DirectPayment from './pages/direct-payment/DirectPayment';
import Coupons from './pages/coupons/Coupons';
import CreateCoupon from './pages/coupons/CreateCoupon';
import EditCoupon from './pages/coupons/EditCoupon';
import Influencers from './pages/influencers/Influencers';
import EditInfluencer from './pages/influencers/EditInfluencer';
import CallerVendors from './pages/caller-menu/CallerVendors';
import CallerTravelPost from './pages/caller-menu/CallerTravelPost';
import CallerPendingTransactions from './pages/caller-menu/CallerPendingTransactions';
import VendorTransactions from './pages/caller-menu/VendorTransactions';
import VendorWallet from './pages/caller-menu/VendorWallet';
import CallLogs from './pages/call-management/CallLogs';
import Country from './pages/locations/Country';
import AddCountry from './pages/locations/AddCountry';
import EditCountry from './pages/locations/EditCountry';
import State from './pages/locations/State';
import AddState from './pages/locations/AddState';
import EditState from './pages/locations/EditState';
import City from './pages/locations/City';
import Location from './pages/locations/Location';
import AddLocation from './pages/locations/AddLocation';
import EditLocation from './pages/locations/EditLocation';
import Months from './pages/locations/Months';
import EditMonth from './pages/locations/EditMonth';
import AddCity from './pages/locations/AddCity';
import ThingsToDo from './pages/locations/ThingsToDo';
import Itinerary from './pages/locations/Itinerary';
import PlacesToVisit from './pages/locations/PlacesToVisit';
import AddThingsToDo from './pages/locations/AddThingsToDo';
import EditThingsToDo from './pages/locations/EditThingsToDo';
import Staff from './pages/staff/Staff';
import AddStaff from './pages/staff/AddStaff';
import EditStaff from './pages/staff/EditStaff';
import ViewLeads from './pages/leads/ViewLeads';
import ViewBlogs from './pages/blogs/ViewBlogs';
import AddBlog from './pages/blogs/AddBlog';
import EditBlog from './pages/blogs/EditBlog';
import TravelPost from './pages/travel-post/TravelPost';
import UnlockList from './pages/unlock-list/UnlockList';
import MaxUnlocked from './pages/max-unlocked/MaxUnlocked';
import ReplacementList from './pages/replacement-list/ReplacementList';
import LeadAnalytics from './pages/lead-analytics/LeadAnalytics';
import ContactUs from './pages/contact-us/ContactUs';
import CorporateInquiries from './pages/corporate-inquiry/CorporateInquiries';
import ItSupport from './pages/it-support/ItSupport';
import OrderList from './pages/order-list/OrderList';
import PackageFacility from './pages/package-facility/PackageFacility';
import Package from './pages/package/Package';
import EditPackage from './pages/package/EditPackage';
import Dmc from './pages/dmc/Dmc';
import EditDmc from './pages/dmc/EditDmc';
import DMCItinerary from './pages/dmc-itinerary/DMCItinerary';
import EditDMCItinerary from './pages/dmc-itinerary/EditDMCItinerary';
import TripCategory from './pages/category/TripCategory';
import Amenity from './pages/amenity/Amenity';
import HotelType from './pages/hotel-type/HotelType';
import Hotel from './pages/hotel/Hotel';
import EditHotel from './pages/hotel/EditHotel';
import VehicleType from './pages/vehicle-type/VehicleType';
import Vehicle from './pages/vehicle/Vehicle';
import EditVehicle from './pages/vehicle/EditVehicle';
import SendEmail from './pages/email/SendEmail';
import SendNotification from './pages/notification/SendNotification';
import Offers from './pages/offers/Offers';
import AddOffer from './pages/offers/AddOffer';
import EditOffer from './pages/offers/EditOffer';
import WhatsNew from './pages/whatsnew/WhatsNew';
import CommonSettings from './pages/common-settings/CommonSettings';
import RechargePlan from './pages/recharge-plan/RechargePlan';
import RewardWinners from './pages/reward/RewardWinners';
import RewardApplication from './pages/reward/RewardApplication';
import RewardPrizes from './pages/reward/RewardPrizes';
import Banner from './pages/banner/Banner';
import Gallery from './pages/gallery/Gallery';
import Suggestions from './pages/suggestion/Suggestions';
import Chat from './pages/chat/Chat';
import Testimonial from './pages/testimonial/Testimonial';
import WebPrivacy from './pages/privacy/WebPrivacy';
import Reviews from './pages/reviews/Reviews';
import AutoBuyList from './pages/autobuy/AutoBuyList';
import StaticPageMetaData from './pages/static-page/StaticPageMetaData';
import BookingList from './pages/booking/BookingList';
import AddBooking from './pages/booking/AddBooking';
import BookingDetail from './pages/booking/BookingDetail';

// CSS imports
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';


// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" /> : children;
};

// App Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/super-admin-login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/staff/login/:vendorId" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />

      {/* Users Routes */}
      <Route path="/users" element={
        <ProtectedRoute>
          <Users />
        </ProtectedRoute>
      } />
      <Route path="/view-users" element={
        <ProtectedRoute>
          <Users />
        </ProtectedRoute>
      } />

      {/* Vendors Routes */}
      <Route path="/vendors" element={
        <ProtectedRoute>
          <Vendors />
        </ProtectedRoute>
      } />
      <Route path="/view-vendors" element={
        <ProtectedRoute>
          <Vendors />
        </ProtectedRoute>
      } />
      <Route path="/vendor/edit/:vendorId" element={
        <ProtectedRoute>
          <VendorEdit />
        </ProtectedRoute>
      } />
      <Route path="/vendor/activity/:vendorId" element={
        <ProtectedRoute>
          <VendorActivity />
        </ProtectedRoute>
      } />
      <Route path="/createTransaction/:vendorId" element={
        <ProtectedRoute>
          <CreateTransaction />
        </ProtectedRoute>
      } />

      {/* Testing Account Route */}
      <Route path="/testing-account" element={
        <ProtectedRoute>
          <TestingAccount />
        </ProtectedRoute>
      } />

      {/* Bank Deposits Route */}
      <Route path="/bank-proof" element={
        <ProtectedRoute>
          <BankDeposits />
        </ProtectedRoute>
      } />

      {/* Direct Payment Route */}
      <Route path="/direct-payment" element={
        <ProtectedRoute>
          <DirectPayment />
        </ProtectedRoute>
      } />

      {/* Coupons Routes */}
      <Route path="/view-coupons" element={
        <ProtectedRoute>
          <Coupons />
        </ProtectedRoute>
      } />
      <Route path="/coupon" element={
        <ProtectedRoute>
          <Coupons />
        </ProtectedRoute>
      } />
      <Route path="/coupon/create" element={
        <ProtectedRoute>
          <CreateCoupon />
        </ProtectedRoute>
      } />
      <Route path="/coupon/edit/:id" element={
        <ProtectedRoute>
          <EditCoupon />
        </ProtectedRoute>
      } />

      {/* Influencers Routes */}
      <Route path="/view-influencers" element={
        <ProtectedRoute>
          <Influencers />
        </ProtectedRoute>
      } />
      <Route path="/influencer/edit/:id" element={
        <ProtectedRoute>
          <EditInfluencer />
        </ProtectedRoute>
      } />

      {/* Caller Menu Routes */}
      <Route path="/caller-vendors" element={
        <ProtectedRoute>
          <CallerVendors />
        </ProtectedRoute>
      } />
      <Route path="/caller-travel-post" element={
        <ProtectedRoute>
          <CallerTravelPost />
        </ProtectedRoute>
      } />
      <Route path="/caller-pending-transactions" element={
        <ProtectedRoute>
          <CallerPendingTransactions />
        </ProtectedRoute>
      } />
      <Route path="/vendorSubscriptionList/:vendorId" element={
        <ProtectedRoute>
          <VendorTransactions />
        </ProtectedRoute>
      } />
      <Route path="/vendorWallet/:vendorId" element={
        <ProtectedRoute>
          <VendorWallet />
        </ProtectedRoute>
      } />

      {/* Call Management Route */}
      <Route path="/call-logs" element={
        <ProtectedRoute>
          <CallLogs />
        </ProtectedRoute>
      } />

      {/* Locations Routes */}
      <Route path="/country" element={
        <ProtectedRoute>
          <Country />
        </ProtectedRoute>
      } />
      <Route path="/add-country" element={
        <ProtectedRoute>
          <AddCountry />
        </ProtectedRoute>
      } />
      <Route path="/edit-country/:id" element={
        <ProtectedRoute>
          <EditCountry />
        </ProtectedRoute>
      } />
      <Route path="/state/:countryId" element={
        <ProtectedRoute>
          <State />
        </ProtectedRoute>
      } />
      <Route path="/add-state/:countryId" element={
        <ProtectedRoute>
          <AddState />
        </ProtectedRoute>
      } />
      <Route path="/edit-state/:id" element={
        <ProtectedRoute>
          <EditState />
        </ProtectedRoute>
      } />
      <Route path="/city/:stateId" element={
        <ProtectedRoute>
          <City />
        </ProtectedRoute>
      } />
      <Route path="/location" element={
        <ProtectedRoute>
          <Location />
        </ProtectedRoute>
      } />
      <Route path="/add-location" element={
        <ProtectedRoute>
          <AddLocation />
        </ProtectedRoute>
      } />
      <Route path="/edit-location/:id" element={
        <ProtectedRoute>
          <EditLocation />
        </ProtectedRoute>
      } />
      <Route path="/view-months" element={
        <ProtectedRoute>
          <Months />
        </ProtectedRoute>
      } />
      <Route path="/edit-month/:id" element={
        <ProtectedRoute>
          <EditMonth />
        </ProtectedRoute>
      } />
      <Route path="/add-city" element={
        <ProtectedRoute>
          <AddCity />
        </ProtectedRoute>
      } />
      <Route path="/things-to-do/:locationId" element={
        <ProtectedRoute>
          <ThingsToDo />
        </ProtectedRoute>
      } />
      <Route path="/itinerary/:locationId" element={
        <ProtectedRoute>
          <Itinerary />
        </ProtectedRoute>
      } />
      <Route path="/places-to-visit/:locationId" element={
        <ProtectedRoute>
          <PlacesToVisit />
        </ProtectedRoute>
      } />
      <Route path="/things-to-do/:locationId/add" element={
        <ProtectedRoute>
          <AddThingsToDo />
        </ProtectedRoute>
      } />
      <Route path="/things-to-do/:locationId/edit/:id" element={
        <ProtectedRoute>
          <EditThingsToDo />
        </ProtectedRoute>
      } />

      {/* Staff Routes */}
      <Route path="/staff" element={
        <ProtectedRoute>
          <Staff />
        </ProtectedRoute>
      } />
      <Route path="/add-staff" element={
        <ProtectedRoute>
          <AddStaff />
        </ProtectedRoute>
      } />
      <Route path="/edit-staff/:id" element={
        <ProtectedRoute>
          <EditStaff />
        </ProtectedRoute>
      } />

      {/* Leads Routes */}
      <Route path="/view-leads" element={
        <ProtectedRoute>
          <ViewLeads />
        </ProtectedRoute>
      } />

      {/* Booking Routes */}
      <Route path="/view-bookings" element={
        <ProtectedRoute>
          <BookingList />
        </ProtectedRoute>
      } />
      <Route path="/add-booking" element={
        <ProtectedRoute>
          <AddBooking />
        </ProtectedRoute>
      } />
      <Route path="/booking-detail/:id" element={
        <ProtectedRoute>
          <BookingDetail />
        </ProtectedRoute>
      } />
      <Route path="/bookings" element={
        <ProtectedRoute>
          <BookingList />
        </ProtectedRoute>
      } />
      <Route path="/booking/create" element={
        <ProtectedRoute>
          <AddBooking />
        </ProtectedRoute>
      } />

      {/* Blogs Routes */}
      <Route path="/view-blogs" element={
        <ProtectedRoute>
          <ViewBlogs />
        </ProtectedRoute>
      } />
      <Route path="/add-blog" element={
        <ProtectedRoute>
          <AddBlog />
        </ProtectedRoute>
      } />
      <Route path="/edit-blog/:id" element={
        <ProtectedRoute>
          <EditBlog />
        </ProtectedRoute>
      } />

      {/* Travel Post Routes */}
      <Route path="/travel-post" element={
        <ProtectedRoute>
          <TravelPost />
        </ProtectedRoute>
      } />
      <Route path="/unlocked-list" element={
        <ProtectedRoute>
          <UnlockList />
        </ProtectedRoute>
      } />
      <Route path="/max-unlocked-list" element={
        <ProtectedRoute>
          <MaxUnlocked />
        </ProtectedRoute>
      } />
      <Route path="/replacement-list" element={
        <ProtectedRoute>
          <ReplacementList />
        </ProtectedRoute>
      } />
      <Route path="/lead-analytics" element={
        <ProtectedRoute>
          <LeadAnalytics />
        </ProtectedRoute>
      } />

      {/* Contact Us Route */}
      <Route path="/view-contactus" element={
        <ProtectedRoute>
          <ContactUs />
        </ProtectedRoute>
      } />

      {/* Corporate Inquiries Route */}
      <Route path="/view-corporate-inquiries" element={
        <ProtectedRoute>
          <CorporateInquiries />
        </ProtectedRoute>
      } />

      {/* IT Support Route */}
      <Route path="/view-itsupport" element={
        <ProtectedRoute>
          <ItSupport />
        </ProtectedRoute>
      } />

      {/* Order List Route */}
      <Route path="/order-list" element={
        <ProtectedRoute>
          <OrderList />
        </ProtectedRoute>
      } />

      {/* AutoBuy Route */}
      <Route path="/autobuy-list" element={
        <ProtectedRoute>
          <AutoBuyList />
        </ProtectedRoute>
      } />

      {/* Package Facility Route */}
      <Route path="/package-facility" element={
        <ProtectedRoute>
          <PackageFacility />
        </ProtectedRoute>
      } />

      {/* Package Routes */}
      <Route path="/view-packages" element={
        <ProtectedRoute>
          <Package />
        </ProtectedRoute>
      } />
      <Route path="/add-package" element={
        <ProtectedRoute>
          <Package />
        </ProtectedRoute>
      } />
      <Route path="/edit-package/:id" element={
        <ProtectedRoute>
          <EditPackage />
        </ProtectedRoute>
      } />

      {/* DMC Routes */}
      <Route path="/view-dmcs" element={
        <ProtectedRoute>
          <Dmc />
        </ProtectedRoute>
      } />
      <Route path="/dmc/create" element={
        <ProtectedRoute>
          <EditDmc />
        </ProtectedRoute>
      } />
      <Route path="/dmc/edit/:id" element={
        <ProtectedRoute>
          <EditDmc />
        </ProtectedRoute>
      } />
      <Route path="/view-dmc-itineraries" element={
        <ProtectedRoute>
          <DMCItinerary />
        </ProtectedRoute>
      } />
      <Route path="/dmc-itinerary/create" element={
        <ProtectedRoute>
          <EditDMCItinerary />
        </ProtectedRoute>
      } />
      <Route path="/dmc-itinerary/edit/:id" element={
        <ProtectedRoute>
          <EditDMCItinerary />
        </ProtectedRoute>
      } />

      {/* Trip Category Route */}
      <Route path="/view-category" element={
        <ProtectedRoute>
          <TripCategory />
        </ProtectedRoute>
      } />

      {/* Amenity Route */}
      <Route path="/view-amenity" element={
        <ProtectedRoute>
          <Amenity />
        </ProtectedRoute>
      } />

      {/* Hotel Type Route */}
      <Route path="/view-hoteltype" element={
        <ProtectedRoute>
          <HotelType />
        </ProtectedRoute>
      } />

      {/* Hotel Routes */}
      <Route path="/hotel" element={
        <ProtectedRoute>
          <Hotel />
        </ProtectedRoute>
      } />
      <Route path="/hotel/create" element={
        <ProtectedRoute>
          <EditHotel />
        </ProtectedRoute>
      } />
      <Route path="/hotel/edit/:id" element={
        <ProtectedRoute>
          <EditHotel />
        </ProtectedRoute>
      } />

      {/* Vehicle Type Route */}
      <Route path="/vehicle-type" element={
        <ProtectedRoute>
          <VehicleType />
        </ProtectedRoute>
      } />

      {/* Vehicle Routes */}
      <Route path="/view-vehicles" element={
        <ProtectedRoute>
          <Vehicle />
        </ProtectedRoute>
      } />
      <Route path="/vehicle/create" element={
        <ProtectedRoute>
          <EditVehicle />
        </ProtectedRoute>
      } />
      <Route path="/vehicle/edit/:id" element={
        <ProtectedRoute>
          <EditVehicle />
        </ProtectedRoute>
      } />

      {/* Email Routes */}
      <Route path="/send-mail" element={
        <ProtectedRoute>
          <SendEmail />
        </ProtectedRoute>
      } />

      {/* Notification Routes */}
      <Route path="/send-notification" element={
        <ProtectedRoute>
          <SendNotification />
        </ProtectedRoute>
      } />

      {/* Offers Routes */}
      <Route path="/view-offers" element={
        <ProtectedRoute>
          <Offers />
        </ProtectedRoute>
      } />
      <Route path="/add-offer" element={
        <ProtectedRoute>
          <AddOffer />
        </ProtectedRoute>
      } />
      <Route path="/edit-offer/:id" element={
        <ProtectedRoute>
          <EditOffer />
        </ProtectedRoute>
      } />

      {/* What's New Routes */}
      <Route path="/view-whatsnew" element={
        <ProtectedRoute>
          <WhatsNew />
        </ProtectedRoute>
      } />

      <Route path="/view-common-settings" element={
        <ProtectedRoute>
          <CommonSettings />
        </ProtectedRoute>
      } />

      {/* Static Page Meta Routes */}
      <Route path="/static-page-meta-data" element={
        <ProtectedRoute>
          <StaticPageMetaData />
        </ProtectedRoute>
      } />

      {/* Reward Routes */}
      <Route path="/view-rewards" element={
        <ProtectedRoute>
          <RewardApplication />
        </ProtectedRoute>
      } />
      <Route path="/view-winners" element={
        <ProtectedRoute>
          <RewardWinners />
        </ProtectedRoute>
      } />
      <Route path="/reward-prizes" element={
        <ProtectedRoute>
          <RewardPrizes />
        </ProtectedRoute>
      } />

      {/* Banner Routes */}
      <Route path="/view-banners" element={
        <ProtectedRoute>
          <Banner />
        </ProtectedRoute>
      } />

      {/* Wall Gallery Routes */}
      <Route path="/view-gallery" element={
        <ProtectedRoute>
          <Gallery />
        </ProtectedRoute>
      } />

      {/* Suggestion Routes */}
      <Route path="/view-suggestions" element={
        <ProtectedRoute>
          <Suggestions />
        </ProtectedRoute>
      } />

      {/* Chat Routes */}
      <Route path="/chats" element={
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      } />

      {/* Testimonial Routes */}
      <Route path="/testimonial" element={
        <ProtectedRoute>
          <Testimonial />
        </ProtectedRoute>
      } />

      {/* Reviews Routes */}
      <Route path="/reviews" element={
        <ProtectedRoute>
          <Reviews />
        </ProtectedRoute>
      } />

      {/* Web Privacy Routes */}
      <Route path="/web-privacy" element={
        <ProtectedRoute>
          <WebPrivacy />
        </ProtectedRoute>
      } />

      {/* Recharge Plan Routes */}
      <Route path="/recharge-plans" element={
        <ProtectedRoute>
          <RechargePlan />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <RefreshProvider>
        <ExtensionErrorGuard />
        <Router>
          <div className="App">
            <AppRoutes />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </div>
        </Router>
      </RefreshProvider>
    </AuthProvider>
  );
}

export default App;
